/**
 * Price rendering (#211 — the display half of #151).
 *
 * Since #151 a menu item's scalar `price` is the REPRESENTATIVE price: when the
 * item has size variants it is the *cheapest* one, derived on write. Rendered
 * bare that is a wrong number, not a partial one — a 20oz drip shown as the
 * 12oz price. Same class as #186, where the detail page printed a confident
 * wrong "Closed" rather than no badge at all.
 *
 * Everything here is pure so the whole module is unit-testable, and it is the
 * single place a price becomes text so the "from" prefix and the unit suffix
 * can't drift between the menu card, the dish page and the restaurant grid.
 */

/**
 * The suffix for a price unit — '/lb', '/L', '' for `each`/absent.
 *
 * DERIVED rather than looked up in a table. The vocabulary lives on the server
 * (`PRICE_UNITS` in `schemas/food.py`) and grows; a hardcoded map here would
 * silently stop covering new units, which is exactly the drift #208 found
 * inside admin, where a hand-written <option> list never offered the metric
 * units added the same day. `l` is the one display exception — a lowercase L
 * reads as a 1.
 */
export function unitSuffix(unit) {
  if (!unit || unit === 'each') return '';
  return `/${unit === 'l' ? 'L' : unit}`;
}

/** A bare amount, no qualifiers. */
export function amount(price) {
  return `$${Number(price).toFixed(2)}`;
}

/**
 * True when the scalar price is the FLOOR OF A RANGE rather than the price.
 *
 * Two or more variants, not one: a single size is a price, and 'from $4.99'
 * over it invents pricier options that don't exist. (Note this differs from
 * admin's current `prices?.length > 0` — see the issue.)
 */
export function hasPriceVariants(item) {
  return (item?.prices?.length ?? 0) > 1;
}

/** Size variants in the vocabulary's own order; ties break on id to stay stable. */
export function orderedPrices(item) {
  return [...(item?.prices ?? [])].sort(
    (a, b) => (a.sort_order - b.sort_order) || (a.id - b.id)
  );
}

/** The display name for one variant, falling back to the slug so it's never nameless. */
export function variantName(variant) {
  return variant?.size_display_name || variant?.size_slug || 'Size';
}

/**
 * The size's name when an item prices EXACTLY ONE (#219), else null.
 *
 * Every multi-size item passes through single-size state and many stop there, so
 * this is the modal case during data entry and a large permanent population — not
 * an edge case. Null at 2+ on purpose: see `sized` below.
 */
export function soleSizeName(item) {
  const prices = item?.prices ?? [];
  if (prices.length !== 1) return null;
  // Deliberately NOT variantName(): its 'Size' fallback exists so a row in a
  // variant LIST is never nameless, but appended to a price it reads as
  // '$4.99 · Size' — noise where the whole point is naming a real size. An
  // unnamed variant should render as a bare price instead, exactly as before.
  return prices[0]?.size_display_name || prices[0]?.size_slug || null;
}

/**
 * Append the size to a rendered price when there is exactly one — '$4.99 · 12 oz'.
 *
 * Without this, "$4.99 for a recorded 12 oz" read identically to "$4.99, size
 * unknown", throwing away a size someone deliberately recorded. #211's `> 1`
 * threshold is right for the 'from' prefix — one size is a price, not a floor —
 * but it was also applied to showing the size AT ALL, which is a different
 * question that was never actually decided.
 *
 * Skipped when the price is a floor: the amount is then the cheapest variant, so
 * naming any single size beside it would label that price with the wrong size.
 */
function sized(text, size, isFloor) {
  if (!size || isFloor) return text;
  return `${text} · ${size}`;
}

/**
 * A menu item's price as a single line — 'from $4.50', '$18.99/lb', '$4.99 · 12 oz'.
 * Returns null when there is no price at all, so callers keep their own
 * "Price varies" wording.
 *
 * The food's base_price fallback deliberately carries NO unit, NO 'from' and NO
 * size: all three describe *this restaurant's* menu item, and asserting them over
 * a catalog-wide base price would be a claim we can't support.
 */
export function menuItemPrice(item) {
  if (item?.price != null) {
    const isFloor = hasPriceVariants(item);
    const prefix = isFloor ? 'from ' : '';
    const base = `${prefix}${amount(item.price)}${unitSuffix(item.price_unit)}`;
    return sized(base, soleSizeName(item), isFloor);
  }
  if (item?.food?.base_price != null) return amount(item.food.base_price);
  return null;
}

/**
 * Summarize a dish's price across the restaurants serving it (#131).
 *
 * `places` are DishPlace rows: { dish_price, dish_price_unit,
 * dish_price_has_variants, dish_price_size }. Falls back to the food's base
 * price, else null so the caller can render its own "Varies".
 *
 * #219 NOTE — a size is named here ONLY when a single place is being summarized.
 * Two places can agree on $4.99 while pouring different sizes, so naming one
 * place's size across several would assert it of all of them: the same category
 * error the unit check below guards against, one field over. A size is only
 * meaningful next to the single item that priced it.
 */
export function dishPriceSummary(places, basePrice) {
  const priced = (places ?? []).filter((p) => p?.dish_price != null);

  if (priced.length > 0) {
    // A min–max span across places that price the dish DIFFERENTLY — one per
    // pound, one each — is not a range, it's a category error. When the places
    // disagree on the unit, decline to summarize rather than print a confident
    // nonsense number.
    const units = new Set(priced.map((p) => p.dish_price_unit || 'each'));
    if (units.size === 1) {
      const unit = priced[0].dish_price_unit;
      const amounts = [...new Set(priced.map((p) => p.dish_price))].sort((a, b) => a - b);

      if (amounts.length === 1) {
        // A single agreed amount can still be a floor, if any place reaching it
        // does so with size variants.
        const isFloor = priced.some((p) => p.dish_price_has_variants);
        const base = `${isFloor ? 'from ' : ''}${amount(amounts[0])}${unitSuffix(unit)}`;
        // Only one place → its size is unambiguously about that price. With
        // several, stay silent (see the note above).
        const size = priced.length === 1 ? priced[0].dish_price_size : null;
        return sized(base, size, isFloor);
      }
      // A span already conveys spread, so no 'from' on top of it.
      return `${amount(amounts[0])} – ${amount(amounts[amounts.length - 1])}${unitSuffix(unit)}`;
    }
    return null;
  }

  if (basePrice != null) return amount(basePrice);
  return null;
}
